import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const FONT = '"Nunito","Baloo 2",sans-serif';
const apiBase = () => process.env.REACT_APP_API_URL || '';
const authHeader = () => {
  const t = localStorage.getItem('accessToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const TYPE_COLORS = {
  vocabulary_topic: { bg: '#eff6ff', accent: '#1e40af', emoji: '🌊' },
  grammar_topic:    { bg: '#f0fdf4', accent: '#15803d', emoji: '🌳' },
};

function MasteryDonut({ pct, color = '#1e40af', size = 64 }) {
  const radius = (size - 8) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (pct / 100) * circ;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={radius} stroke="#e5e7eb" strokeWidth="6" fill="none" />
      <circle cx={size/2} cy={size/2} r={radius} stroke={color} strokeWidth="6" fill="none"
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round" transform={`rotate(-90 ${size/2} ${size/2})`} />
      <text x="50%" y="54%" textAnchor="middle" fontSize="13" fontWeight="700" fill="#1f2937" fontFamily={FONT}>
        {pct}%
      </text>
    </svg>
  );
}

function TopicCard({ t, onClick }) {
  const palette = TYPE_COLORS[t.tag_type_code] || TYPE_COLORS.vocabulary_topic;
  const stats = t.stats;
  const reviewBadge = stats.review_due > 0;
  return (
    <button
      onClick={onClick}
      style={{
        textAlign: 'left',
        background: palette.bg,
        border: `2px solid ${palette.accent}22`,
        borderRadius: 16,
        padding: 16,
        cursor: 'pointer',
        fontFamily: FONT,
        position: 'relative',
        transition: 'transform 0.1s, box-shadow 0.1s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <div style={{ fontSize: 32 }}>{palette.emoji}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {t.name}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
            {stats.total} từ • {stats.mastered} đã thuộc
          </div>
        </div>
        <MasteryDonut pct={stats.mastery_pct} color={palette.accent} />
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 12 }}>
        {reviewBadge ? (
          <span style={badge('#ef4444', '#fef2f2')}>🔴 {stats.review_due} cần ôn</span>
        ) : null}
        {stats.in_progress > 0 ? (
          <span style={badge('#f59e0b', '#fffbeb')}>🟡 {stats.in_progress} đang học</span>
        ) : null}
        {stats.unexplored > 0 ? (
          <span style={badge('#6b7280', '#f9fafb')}>⚪ {stats.unexplored} chưa học</span>
        ) : null}
      </div>
    </button>
  );
}

function badge(color, bg) {
  return {
    padding: '3px 8px',
    borderRadius: 6,
    color, background: bg,
    fontWeight: 600,
  };
}

export default function TopicListPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('all'); // all | vocabulary | grammar
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const url = `${apiBase()}/client/topics/parents${tab !== 'all' ? `?tag_type=${tab}` : ''}`;
      const r = await fetch(url, { headers: authHeader() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      setTopics(d.data || []);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [tab]);

  return (
    <div style={{ padding: 24, fontFamily: FONT, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 16 }}>
        <Link to="/dashboard" style={{ color: '#1e40af', fontWeight: 600 }}>← Dashboard</Link>
      </div>
      <h1 style={{ fontSize: 28, fontWeight: 800, marginBottom: 6 }}>📚 Chủ điểm</h1>
      <p style={{ color: '#6b7280', marginBottom: 18 }}>
        Học theo chủ điểm — hệ thống sẽ trộn các từ bạn cần ôn + chưa biết để tạo bài.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        {['all', 'vocabulary', 'grammar'].map((k) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: 'none',
              background: tab === k ? '#1e40af' : '#e5e7eb',
              color: tab === k ? '#fff' : '#374151',
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: FONT,
            }}
          >
            {k === 'all' ? 'Tất cả' : k === 'vocabulary' ? '🌊 Từ vựng' : '🌳 Ngữ pháp'}
          </button>
        ))}
      </div>

      {error ? <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8 }}>⚠ {error}</div> : null}
      {loading ? <div style={{ padding: 32, textAlign: 'center' }}>Đang tải...</div> : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
        {topics.map((t) => (
          <TopicCard key={t.tag_id} t={t} onClick={() => navigate(`/topics/${t.tag_id}`)} />
        ))}
      </div>
      {!loading && topics.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
          Không có chủ điểm nào.
        </div>
      ) : null}
    </div>
  );
}
